import React, { useState, useEffect } from 'react';
import { ArrowLeft, BarChart3, TrendingUp, DollarSign, Percent, ChevronDown, ChevronUp, Settings, RefreshCw, BarChart2, Save } from 'lucide-react';
import { useCalculator } from '../context/CalculatorContext';
import { compoundMultiplierContent } from '../data/compoundMultiplierContent';
import { formatCurrency, formatPercent, shortenLargeNumber } from '../utils/formatters';
import { motion } from 'framer-motion';
import ChartSeriesToggleCard from './ui/ChartSeriesToggleCard';
import Slider from './ui/Slider';
import { Toggle } from './ui/Toggle';
import DynamicChartSystem from './charts/DynamicChartSystem';
import ExcelTableV5 from './ExcelTableV5';
import DownloadReportButton from './DownloadReportButton';
import MultimediaControls from './ui/MultimediaControls';
import CompoundInterestMultiplier from './compoundInterest/CompoundInterestMultiplier';

interface ComparativeAnalysisProps {
  onBack: () => void;
}

const ComparativeAnalysis: React.FC<ComparativeAnalysisProps> = ({ onBack }) => {
  const { 
    investment, 
    updateInvestment, 
    results, 
    toggleReinvest,
    patrimonyChartVisibility,
    incomeChartVisibility,
    cashFlowChartVisibility,
    comparisonChartVisibility,
    togglePatrimonyChartElement,
    toggleIncomeChartElement,
    toggleCashFlowChartElement,
    toggleComparisonChartElement,
    resetPatrimonyChartVisibility,
    resetIncomeChartVisibility,
    resetCashFlowChartVisibility,
    resetComparisonChartVisibility
  } = useCalculator();
  
  const [isConfigExpanded, setIsConfigExpanded] = useState(true);
  const [activeTab, setActiveTab] = useState<'chart' | 'compoundInterest' | 'excel'>('chart');
  const [activeChartCategory, setActiveChartCategory] = useState<'patrimony' | 'income' | 'cashflow' | 'comparison'>('patrimony');
  const [showExcelTable, setShowExcelTable] = useState(false);
  
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Prepare data for charts
  const prepareChartData = () => {
    if (!results) return null;

    // Patrimony chart data
    const allPatrimonyData = results.yearlyData.map(data => ({
      year: data.year,
      'Cosecha Capital': patrimonyChartVisibility.showReal ? data.realCitrusPatrimony : data.citrusPatrimony,
      'CETES': patrimonyChartVisibility.showReal ? data.realCetesPatrimony : data.cetesPatrimony,
      'Ahorro Tradicional': patrimonyChartVisibility.showReal ? data.realSavingsPatrimony : data.savingsPatrimony,
      'Bienes Raíces': patrimonyChartVisibility.showReal ? data.realEstateRealPatrimony : data.realEstatePatrimony
    }));

    // Income chart data
    const allIncomeData = results.yearlyData.map(data => ({
      year: data.year,
      'Cosecha Capital': incomeChartVisibility.showReal ? data.realCitrusIncome : data.citrusIncome,
      'CETES': data.cetesIncome,
      'Ahorro Tradicional': data.savingsIncome,
      'Bienes Raíces': data.realEstateIncome
    }));

    // Cash flow chart data
    const allCashflowData = results.yearlyData.map(data => ({
      year: data.year,
      'Flujo Cosecha Capital': data.availableCashFlow,
      'Flujo CETES': data.cetesCashFlow || 0,
      'Flujo Ahorro': data.savingsCashFlow || 0,
      'Flujo Bienes Raíces': data.realEstateCashFlow || 0
    }));

    // Comparison chart data (final values)
    const comparisonData = [
      {
        investment: 'Cosecha Capital',
        value: comparisonChartVisibility.showReal ? results.realFinalPatrimony : results.finalPatrimony,
        percentage: 100
      },
      {
        investment: 'CETES',
        value: comparisonChartVisibility.showReal ? results.realCetesPatrimony : results.cetesPatrimony,
        percentage: ((comparisonChartVisibility.showReal ? results.realCetesPatrimony : results.cetesPatrimony) / 
                    (comparisonChartVisibility.showReal ? results.realFinalPatrimony : results.finalPatrimony)) * 100
      },
      {
        investment: 'Ahorro Tradicional',
        value: comparisonChartVisibility.showReal ? results.realSavingsPatrimony : results.savingsPatrimony,
        percentage: ((comparisonChartVisibility.showReal ? results.realSavingsPatrimony : results.savingsPatrimony) / 
                    (comparisonChartVisibility.showReal ? results.realFinalPatrimony : results.finalPatrimony)) * 100
      },
      {
        investment: 'Bienes Raíces',
        value: comparisonChartVisibility.showReal ? results.realRealEstatePatrimony : results.realEstatePatrimony,
        percentage: ((comparisonChartVisibility.showReal ? results.realRealEstatePatrimony : results.realEstatePatrimony) / 
                    (comparisonChartVisibility.showReal ? results.realFinalPatrimony : results.finalPatrimony)) * 100
      }
    ];
    
    return {
      patrimony: allPatrimonyData,
      income: allIncomeData,
      cashflow: allCashflowData,
      comparison: comparisonData
    };
  };

  const chartData = prepareChartData();

  // Chart categories
  const chartCategories = [
    {
      id: 'patrimony',
      title: 'Evolución del Patrimonio',
      description: 'Crecimiento patrimonial a lo largo del tiempo',
      icon: <TrendingUp className="w-5 h-5" />,
      color: 'from-green-500 to-green-600',
      availableTypes: ['line', 'bar', 'area', 'pie'] as const
    },
    {
      id: 'income',
      title: 'Evolución de Ingresos',
      description: 'Ingresos anuales generados por la inversión',
      icon: <DollarSign className="w-5 h-5" />,
      color: 'from-blue-500 to-blue-600',
      availableTypes: ['line', 'bar', 'area'] as const
    },
    {
      id: 'cashflow',
      title: 'Flujo de Efectivo',
      description: 'Análisis de flujo de efectivo disponible',
      icon: <TrendingUp className="w-5 h-5" />,
      color: 'from-purple-500 to-purple-600',
      availableTypes: ['line', 'bar', 'area'] as const
    },
    {
      id: 'comparison',
      title: 'Comparativa de Inversiones',
      description: 'Comparación del patrimonio final entre diferentes opciones',
      icon: <BarChart3 className="w-5 h-5" />,
      color: 'from-orange-500 to-orange-600',
      availableTypes: ['bar', 'pie'] as const
    }
  ];

  // Chart elements
  const chartElements = {
    patrimony: [
      {
        dataKey: 'Cosecha Capital',
        name: `Cosecha Capital ${patrimonyChartVisibility.showReal ? '(Real)' : ''}`,
        color: '#16a34a',
        strokeWidth: 3,
        fillOpacity: 0.6
      },
      {
        dataKey: 'CETES',
        name: 'CETES',
        color: '#0284c7',
        strokeWidth: 2,
        fillOpacity: 0.4
      },
      {
        dataKey: 'Ahorro Tradicional',
        name: 'Ahorro Tradicional',
        color: '#7c3aed',
        strokeWidth: 2,
        fillOpacity: 0.4
      },
      {
        dataKey: 'Bienes Raíces',
        name: 'Bienes Raíces',
        color: '#ea580c',
        strokeWidth: 2,
        fillOpacity: 0.4
      }
    ],
    income: [
      {
        dataKey: 'Cosecha Capital',
        name: `Cosecha Capital ${incomeChartVisibility.showReal ? '(Real)' : ''}`,
        color: '#16a34a',
        strokeWidth: 3,
        fillOpacity: 0.6
      },
      {
        dataKey: 'CETES',
        name: 'CETES',
        color: '#0284c7',
        strokeWidth: 2,
        fillOpacity: 0.4
      },
      {
        dataKey: 'Ahorro Tradicional',
        name: 'Ahorro Tradicional',
        color: '#7c3aed',
        strokeWidth: 2,
        fillOpacity: 0.4
      },
      {
        dataKey: 'Bienes Raíces',
        name: 'Bienes Raíces',
        color: '#ea580c',
        strokeWidth: 2,
        fillOpacity: 0.4
      }
    ],
    cashflow: [
      {
        dataKey: 'Flujo Cosecha Capital',
        name: 'Cosecha Capital',
        color: '#16a34a',
        strokeWidth: 3,
        fillOpacity: 0.6
      },
      {
        dataKey: 'Flujo CETES',
        name: 'CETES',
        color: '#0284c7',
        strokeWidth: 2,
        fillOpacity: 0.4
      },
      {
        dataKey: 'Flujo Ahorro',
        name: 'Ahorro Tradicional',
        color: '#7c3aed',
        strokeWidth: 2,
        fillOpacity: 0.4
      },
      {
        dataKey: 'Flujo Bienes Raíces',
        name: 'Bienes Raíces',
        color: '#ea580c',
        strokeWidth: 2,
        fillOpacity: 0.4
      }
    ],
    comparison: [
      {
        dataKey: 'value',
        name: 'Patrimonio Final',
        color: '#16a34a',
        strokeWidth: 2,
        fillOpacity: 0.6
      }
    ]
  };

  // Pie data for comparison chart
  const pieData = {
    comparison: chartData?.comparison.filter(item => 
      (item.investment === 'Cosecha Capital' && comparisonChartVisibility.cosechaCapital) ||
      (item.investment === 'CETES' && comparisonChartVisibility.cetes) ||
      (item.investment === 'Ahorro Tradicional' && comparisonChartVisibility.savings) ||
      (item.investment === 'Bienes Raíces' && comparisonChartVisibility.realEstate)
    ).map(item => ({
      name: item.investment,
      value: item.value,
      color: item.investment === 'Cosecha Capital' ? '#16a34a' : 
             item.investment === 'CETES' ? '#0284c7' : 
             item.investment === 'Ahorro Tradicional' ? '#7c3aed' : 
             '#ea580c'
    }))
  };

  // Summary data
  const summaryData = {
    patrimony: [
      {
        label: `Cosecha Capital ${patrimonyChartVisibility.showReal ? '(Real)' : ''}`,
        value: results ? formatCurrency(patrimonyChartVisibility.showReal ? results.realFinalPatrimony : results.finalPatrimony, investment.currencyFormat) : '-',
        color: 'text-green-600 bg-green-50'
      },
      {
        label: 'CETES',
        value: results ? formatCurrency(patrimonyChartVisibility.showReal ? results.realCetesPatrimony : results.cetesPatrimony, investment.currencyFormat) : '-',
        color: 'text-blue-600 bg-blue-50'
      },
      {
        label: 'Ahorro',
        value: results ? formatCurrency(patrimonyChartVisibility.showReal ? results.realSavingsPatrimony : results.savingsPatrimony, investment.currencyFormat) : '-',
        color: 'text-purple-600 bg-purple-50'
      },
      {
        label: 'Bienes Raíces',
        value: results ? formatCurrency(patrimonyChartVisibility.showReal ? results.realRealEstatePatrimony : results.realEstatePatrimony, investment.currencyFormat) : '-',
        color: 'text-orange-600 bg-orange-50'
      }
    ],
    income: [
      {
        label: 'Cosecha Capital',
        value: results ? formatCurrency(results.finalMonthlyIncome * 12, investment.currencyFormat) : '-',
        color: 'text-green-600 bg-green-50'
      },
      {
        label: 'CETES',
        value: results ? formatCurrency(results.cetesPatrimony * (investment.cetesRate / 100), investment.currencyFormat) : '-',
        color: 'text-blue-600 bg-blue-50'
      },
      {
        label: 'Ahorro',
        value: results ? formatCurrency(results.savingsPatrimony * (investment.savingsRate / 100), investment.currencyFormat) : '-',
        color: 'text-purple-600 bg-purple-50'
      },
      {
        label: 'Bienes Raíces',
        value: results ? formatCurrency(results.realEstatePatrimony * (investment.realEstateRent / 100), investment.currencyFormat) : '-',
        color: 'text-orange-600 bg-orange-50'
      }
    ],
    cashflow: [
      {
        label: 'Flujo Final',
        value: results ? formatCurrency(results.yearlyData[results.yearlyData.length - 1]?.availableCashFlow || 0, investment.currencyFormat) : '-',
        color: 'text-green-600 bg-green-50'
      },
      {
        label: 'Año Recuperación',
        value: results && results.paybackYear ? `Año ${results.paybackYear}` : 'N/A',
        color: 'text-blue-600 bg-blue-50'
      },
      {
        label: 'CAGR',
        value: results ? formatPercent(results.cagr * 100) : '-',
        color: 'text-purple-600 bg-purple-50'
      },
      {
        label: 'Multiplicador',
        value: results ? `${results.capitalMultiplier.toFixed(1)}x` : '-',
        color: 'text-orange-600 bg-orange-50'
      }
    ],
    comparison: [
      {
        label: 'Cosecha Capital',
        value: results ? formatCurrency(results.finalPatrimony, investment.currencyFormat) : '-',
        color: 'text-green-600 bg-green-50'
      },
      {
        label: 'CETES',
        value: results ? formatCurrency(results.cetesPatrimony, investment.currencyFormat) : '-',
        color: 'text-blue-600 bg-blue-50'
      },
      {
        label: 'Ahorro',
        value: results ? formatCurrency(results.savingsPatrimony, investment.currencyFormat) : '-',
        color: 'text-purple-600 bg-purple-50'
      },
      {
        label: 'Bienes Raíces',
        value: results ? formatCurrency(results.realEstatePatrimony, investment.currencyFormat) : '-',
        color: 'text-orange-600 bg-orange-50'
      }
    ]
  };

  // Additional info for cashflow chart
  const additionalInfo = {
    cashflow: results && results.paybackYear ? (
      <div className="p-4 bg-green-50 rounded-lg">
        <p className="text-sm text-green-800">
          <strong>Año de Recuperación Estimado:</strong> Año {results.paybackYear}
        </p>
        <p className="text-xs text-green-600 mt-1">
          A partir de este año, la inversión comienza a generar ganancias netas.
        </p>
      </div>
    ) : null
  };

  // Visibility controls for DynamicChartSystem
  const visibilityControls = {
    patrimony: {
      visibility: patrimonyChartVisibility,
      toggle: togglePatrimonyChartElement
    },
    income: {
      visibility: incomeChartVisibility,
      toggle: toggleIncomeChartElement
    },
    cashflow: {
      visibility: cashFlowChartVisibility,
      toggle: toggleCashFlowChartElement
    },
    comparison: {
      visibility: comparisonChartVisibility,
      toggle: toggleComparisonChartElement
    }
  };

  // Reset functions for DynamicChartSystem
  const resetVisibilityFunctions = {
    patrimony: resetPatrimonyChartVisibility,
    income: resetIncomeChartVisibility,
    cashflow: resetCashFlowChartVisibility,
    comparison: resetComparisonChartVisibility
  };

  if (showExcelTable) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 p-8">
        <div className="container mx-auto">
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-semibold text-blue-800">Tabla de Evolución Detallada</h2>
              <button
                onClick={() => setShowExcelTable(false)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
              >
                <ArrowLeft className="w-5 h-5" />
                <span>Volver a Comparativa</span>
              </button>
            </div>
            
            <ExcelTableV5 
              initialCertificates={investment.initialCertificates} 
              years={investment.years}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 p-8">
      <div className="container mx-auto">
        {/* Back Button */}
        <button
          onClick={onBack}
          className="mb-6 flex items-center gap-2 px-4 py-2 bg-white text-gray-700 rounded-lg shadow-sm hover:bg-gray-50 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Volver al Inicio</span>
        </button>
        
        {/* Main Content */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-blue-800 mb-2">Multiplicador™ vs Mercado Tradicional</h1>
          <h2 className="text-xl font-semibold text-green-700 mb-2">{compoundMultiplierContent.title}</h2>
          <p className="text-gray-600">{compoundMultiplierContent.tagline}</p>
          
          <div className="mt-4 p-4 bg-gradient-to-br from-green-50 to-emerald-100 rounded-xl border border-green-200">
            <p className="text-sm text-green-700">
              <strong>La Diferencia:</strong> Mientras las inversiones tradicionales solo multiplican dinero, 
              el Multiplicador™ multiplica activos reales productivos que generan ingresos permanentes.
            </p>
          </div>
            
            {/* Disclaimer */}
            <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg max-w-4xl mx-auto">
              <p className="text-xs text-yellow-800">
                <strong>⚠️ Aviso:</strong> Las proyecciones mostradas son estimaciones y no garantizan rendimientos futuros.
              </p>
            </div>
          </div>
          
          {/* Configuration Section */}
          <div className="mb-8">
            <button
              onClick={() => setIsConfigExpanded(!isConfigExpanded)}
              className="w-full flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <div className="flex items-center gap-3">
                <Settings className="w-5 h-5 text-blue-600" />
                <span className="font-medium text-gray-700">Configuración de Comparación</span>
              </div>
              {isConfigExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </button>
            
            {isConfigExpanded && (
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-6 p-4 bg-gray-50 rounded-lg">
                {/* Initial Investment */}
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-800">Inversión Inicial</h3>
                  
                  <Slider
                    label="Certificados"
                    value={investment.initialCertificates}
                    min={1}
                    max={20}
                    step={1}
                    onChange={(value) => updateInvestment({ initialCertificates: value })}
                    formatter={(value) => `${value} certificados`}
                  />
                  
                  <Slider
                    label="Horizonte de Inversión"
                    value={investment.years}
                    min={5}
                    max={30}
                    step={1}
                    onChange={(value) => updateInvestment({ years: value })}
                    formatter={(value) => `${value} años`}
                  />
                  
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <p className="text-sm text-blue-700">
                      Inversión total: {formatCurrency(investment.initialCertificates * investment.certificateBasePrice, investment.currencyFormat)}
                    </p>
                  </div>
                </div>
                
                {/* Cosecha Capital Settings */}
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-800">Cosecha Capital</h3>
                  
                  <Slider
                    label="Utilidad Anual"
                    value={investment.annualProfit}
                    min={10}
                    max={30}
                    step={1}
                    onChange={(value) => updateInvestment({ annualProfit: value })}
                    formatter={(value) => `${value}%`}
                  />
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <RefreshCw className="w-5 h-5 text-green-600" />
                      <span className="text-sm font-medium text-gray-700">Reinvertir Utilidades</span>
                    </div>
                    <ChartSeriesToggleCard
                      label=""
                      icon={RefreshCw}
                      color="#16a34a"
                      checked={investment.reinvestProfits}
                      onClick={toggleReinvest}
                      className="w-32"
                    />
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-yellow-600" />
                      <span className="text-sm font-medium text-gray-700">Incremento Precio Limón</span>
                    </div>
                    <ChartSeriesToggleCard
                      label=""
                      icon={TrendingUp}
                      color="#f59e0b"
                      checked={investment.increaseLemonPrice}
                      onClick={() => updateInvestment({ increaseLemonPrice: !investment.increaseLemonPrice })}
                      className="w-32"
                    />
                  </div>
                  
                  {investment.increaseLemonPrice && (
                    <Slider
                      label="Incremento Anual Precio Limón"
                      value={investment.lemonPriceIncrease}
                      min={1}
                      max={10}
                      step={0.5}
                      onChange={(value) => updateInvestment({ lemonPriceIncrease: value })}
                      formatter={(value) => `${value}%`}
                    />
                  )}
                </div>
                
                {/* Comparative Investments */}
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-800">Inversiones Comparativas</h3>
                  
                  <Slider
                    label="CETES"
                    value={investment.cetesRate}
                    min={1}
                    max={15}
                    step={0.5}
                    onChange={(value) => updateInvestment({ cetesRate: value })}
                    formatter={(value) => `${value}%`}
                  />
                  
                  <Slider
                    label="Ahorro Tradicional"
                    value={investment.savingsRate}
                    min={1}
                    max={15}
                    step={0.5}
                    onChange={(value) => updateInvestment({ savingsRate: value })}
                    formatter={(value) => `${value}%`}
                  />
                  
                  <Slider
                    label="Bienes Raíces - Apreciación"
                    value={investment.realEstateAppreciation}
                    min={1}
                    max={15}
                    step={0.5}
                    onChange={(value) => updateInvestment({ realEstateAppreciation: value })}
                    formatter={(value) => `${value}%`}
                  />
                  
                  <Slider
                    label="Bienes Raíces - Renta"
                    value={investment.realEstateRent}
                    min={0}
                    max={15}
                    step={0.5}
                    onChange={(value) => updateInvestment({ realEstateRent: value })}
                    formatter={(value) => `${value}%`}
                  />
                </div>
                
                {/* Other Settings */}
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-800">Otros Ajustes</h3>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <DollarSign className="w-5 h-5 text-red-600" />
                      <span className="text-sm font-medium text-gray-700">Aplicar Impuestos</span>
                    </div>
                    <ChartSeriesToggleCard
                      label=""
                      icon={DollarSign}
                      color="#dc2626"
                      checked={investment.applyTaxes}
                      onClick={() => updateInvestment({ applyTaxes: !investment.applyTaxes })}
                      className="w-32"
                    />
                  </div>
                  
                  {investment.applyTaxes && (
                    <Slider
                      label="Tasa de Impuestos"
                      value={investment.taxRate}
                      min={0}
                      max={50}
                      step={1}
                      onChange={(value) => updateInvestment({ taxRate: value })}
                      formatter={(value) => `${value}%`}
                    />
                  )}
                  
                  <Slider
                    label="Tasa de Inflación"
                    value={investment.inflationRate}
                    min={0}
                    max={15}
                    step={0.5}
                    onChange={(value) => updateInvestment({ inflationRate: value })}
                    formatter={(value) => `${value}%`}
                  />
                  
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">Mostrar Valores Reales</span>
                    <Toggle
                      checked={patrimonyChartVisibility.showReal}
                      onChange={() => togglePatrimonyChartElement('showReal')}
                      label=""
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
          
          {/* Chart and Excel Table Buttons */}
          <div className="flex justify-between items-center mb-4">
            <div className="flex space-x-2">
              <button
                onClick={() => setActiveTab('chart')}
                className={`px-4 py-2 rounded-lg ${
                  activeTab === 'chart' 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Gráficos
              </button>
              <button
                onClick={() => setActiveTab('compoundInterest')}
                className={`px-4 py-2 rounded-lg ${
                  activeTab === 'compoundInterest' 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Interés Compuesto
              </button>
              <button
                onClick={() => setShowExcelTable(true)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                Tabla Excel
              </button>
            </div>
            <DownloadReportButton />
          </div>
          
          {/* Dynamic Chart System */}
          {activeTab === 'chart' && chartData && (
            <div className="bg-white rounded-xl shadow-md p-6 mb-8">
              <DynamicChartSystem
                categories={chartCategories}
                chartData={{
                  patrimony: chartData.patrimony,
                  income: chartData.income,
                  cashflow: chartData.cashflow,
                  comparison: chartData.comparison
                }}
                chartElements={chartElements}
                pieData={pieData}
                formatters={{
                  yLabel: (value) => shortenLargeNumber(value),
                  tooltip: (value) => formatCurrency(value, investment.currencyFormat)
                }}
                summaryData={summaryData}
                additionalInfo={additionalInfo}
                investment={investment}
                multiplierValidChartIds={['patrimony', 'comparison']}
                visibilityControls={visibilityControls}
                resetVisibilityFunctions={resetVisibilityFunctions}
                hideAdditionalOptions={true}
              />
            </div>
          )}
          
          {/* Compound Interest Multiplier */}
          {activeTab === 'compoundInterest' && (
            <div className="bg-white rounded-xl shadow-md p-6 mb-8">
              <CompoundInterestMultiplier />
            </div>
          )}
          
        </div>
      </div>
    </div>
  );
};

export default ComparativeAnalysis;